import React from 'react';
import { Button } from '../../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { FileText, Plus, Download, Trash2 } from 'lucide-react';
import { getCategoryLabel, formatSize } from './fileConstants';
import { getFileIcon } from './fileHelpers';

/**
 * Filing Cabinet Card Component
 * Displays documents in a list format
 */
const FilingCabinetCard = ({ 
  documentFiles, 
  onAddFile, 
  onFileClick, 
  onDownload, 
  onDelete, 
  uploading 
}) => {
  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <FileText className="w-4 h-4" /> Filing Cabinet
          </CardTitle>
          <Button 
            variant="outline" 
            size="sm"
            onClick={onAddFile}
            disabled={uploading}
          >
            <Plus className="w-4 h-4 mr-1" /> Add File
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {documentFiles.length === 0 ? (
          <div className="text-center py-8 border-2 border-dashed rounded-lg">
            <FileText className="w-10 h-10 mx-auto mb-2 text-muted-foreground/30" />
            <p className="text-sm text-muted-foreground">No documents yet</p>
          </div>
        ) : (
          <div className="space-y-2">
            {documentFiles.map((file) => (
              <div 
                key={file.id} 
                className="flex items-center justify-between p-3 border rounded-md bg-muted/20 hover:bg-muted/40 cursor-pointer transition-colors"
                onClick={(e) => onFileClick(file, e)}
                data-testid={`file-row-${file.id}`}
              >
                <div className="flex items-center gap-3">
                  {getFileIcon(file.mime_type)}
                  <div>
                    <p className="text-sm font-medium">{file.description || file.original_filename}</p>
                    <p className="text-xs text-muted-foreground">
                      {getCategoryLabel(file.category) && <span className="text-primary">{getCategoryLabel(file.category)} • </span>}
                      {formatSize(file.file_size_bytes)} • {new Date(file.created_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  {file.published_to_portal && (
                    <div className="bg-primary text-white text-[9px] px-1.5 py-0.5 rounded font-medium mr-1">Published</div>
                  )}
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={(e) => { e.stopPropagation(); onDownload(file.id, file.original_filename, e); }}
                  >
                    <Download className="w-4 h-4" />
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="text-destructive" 
                    onClick={(e) => { e.stopPropagation(); onDelete(file.id, e); }}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default FilingCabinetCard;
